/**
 * Universal Video Embed Utility
 * Supports Google Drive, YouTube, Vimeo, Cloudflare Stream, Bunny.net, and direct MP4/WebM videos.
 */

export function getVideoEmbedInfo(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') {
    return {
      provider: null,
      embedUrl: null,
      isValid: false,
      label: 'No URL',
      color: 'text-zinc-500',
      badgeClass: 'bg-zinc-800 text-zinc-400 border-zinc-700',
      isDrive: false
    };
  }

  const url = rawUrl.trim();

  // 1. Google Drive
  // Formats:
  // https://drive.google.com/file/d/1A2B3C4D5E.../view?usp=sharing
  // https://drive.google.com/file/d/1A2B3C4D5E.../preview
  // https://drive.google.com/open?id=1A2B3C4D5E...
  // https://drive.google.com/uc?id=1A2B3C4D5E...
  const driveMatch = url.match(/drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?id=)([a-zA-Z0-9_-]+)/);
  if (driveMatch && driveMatch[1]) {
    const fileId = driveMatch[1];
    return {
      provider: 'drive',
      fileId,
      embedUrl: `https://drive.google.com/file/d/${fileId}/preview`,
      isValid: true,
      label: 'Google Drive Private Video',
      color: 'text-[#00FF88]',
      badgeClass: 'bg-[#00FF88]/10 text-[#00FF88] border-[#00FF88]/30',
      isDrive: true
    };
  }

  // 2. YouTube
  // Formats:
  // https://www.youtube.com/watch?v=VIDEO_ID
  // https://youtu.be/VIDEO_ID
  // https://www.youtube.com/embed/VIDEO_ID
  // https://www.youtube.com/shorts/VIDEO_ID
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch && ytMatch[1]) {
    const videoId = ytMatch[1];
    return {
      provider: 'youtube',
      videoId,
      embedUrl: `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&modestbranding=1`,
      isValid: true,
      label: 'YouTube Video',
      color: 'text-[#FF3B30]',
      badgeClass: 'bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30',
      isDrive: false
    };
  }

  // 3. Vimeo
  // Format: https://vimeo.com/123456789
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch && vimeoMatch[1]) {
    const videoId = vimeoMatch[1];
    return {
      provider: 'vimeo',
      videoId,
      embedUrl: `https://player.vimeo.com/video/${videoId}?color=00d991&title=0&byline=0`,
      isValid: true,
      label: 'Vimeo Video',
      color: 'text-[#00F0FF]',
      badgeClass: 'bg-[#00F0FF]/10 text-[#00F0FF] border-[#00F0FF]/30',
      isDrive: false
    };
  }

  // 4. Direct video files (.mp4, .webm, .m3u8)
  if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url)) {
    return {
      provider: 'direct',
      embedUrl: url,
      isValid: true,
      label: 'Direct HTML5 Video (MP4)',
      color: 'text-[#A855F7]',
      badgeClass: 'bg-[#A855F7]/10 text-[#A855F7] border-[#A855F7]/30',
      isDrive: false
    };
  }

  // 5. Zoom Cloud Recording
  // Formats:
  // https://us06web.zoom.us/rec/play/...
  // https://us06web.zoom.us/rec/share/...
  // https://zoom.us/rec/...
  if (url.includes('zoom.us/rec') || url.includes('.zoom.us/rec')) {
    return {
      provider: 'zoom',
      embedUrl: url,
      rawUrl: url,
      isValid: true,
      label: 'Zoom Cloud Recording',
      color: 'text-[#2D8CFF]',
      badgeClass: 'bg-[#2D8CFF]/10 text-[#2D8CFF] border-[#2D8CFF]/30',
      isZoom: true,
      isDrive: false
    };
  }

  // 6. Existing embed iframe links (Cloudflare Stream, Bunny, Loom, etc.)
  if (url.includes('/embed') || url.includes('/preview') || url.includes('cloudflarestream.com') || url.includes('b-cdn.net')) {
    return {
      provider: 'iframe',
      embedUrl: url,
      isValid: true,
      label: 'Stream Embed Player',
      color: 'text-[#FF9500]',
      badgeClass: 'bg-[#FF9500]/10 text-[#FF9500] border-[#FF9500]/30',
      isDrive: false
    };
  }

  // Generic fallback if user enters any valid http link
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return {
      provider: 'generic',
      embedUrl: url,
      isValid: true,
      label: 'Custom Web Link',
      color: 'text-zinc-300',
      badgeClass: 'bg-white/10 text-white border-white/20',
      isDrive: false
    };
  }

  return {
    provider: 'unknown',
    embedUrl: null,
    isValid: false,
    label: 'Invalid URL',
    color: 'text-[#FF3B30]',
    badgeClass: 'bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30',
    isDrive: false
  };
}
